import React, { useState } from 'react';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './button';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logBitacora } from '../utils/bitacora';
import { mutateContract } from '../utils/contractWrites';
import CompactFileReceiver from './CompactFileReceiver';
import { EndosoDialog } from './EndosoDialog';
import {
  DOCUMENTO_TIPOS,
  DOCUMENTO_TIPO_LABELS,
  EVENTO_TIPOS,
  buildEvento,
  applyEndoso,
  getEstadoFianza,
  ESTADO_FIANZA,
} from '../utils/fianzaEventos';
import { recordComisionEvento } from '../api/comisiones';
import { contratoStorageBase } from '../utils/contratoStorage';

const sanitize = (name) => (name || 'archivo').replace(/[^\w.-]+/g, '_');

// Terminal event applied when attaching a given documento tipo.
const TERMINAL_FOR_DOC = {
  [DOCUMENTO_TIPOS.ACTA_ENTREGA]: EVENTO_TIPOS.CANCELACION,
  [DOCUMENTO_TIPOS.ANULACION]: EVENTO_TIPOS.ANULACION,
};

// Accent colour per documento tipo.
const DOC_ACCENT = {
  acta_entrega: 'border-l-red-500',
  anulacion: 'border-l-red-500',
  convenio_modificatorio: 'border-l-cian-digital',
  otro: 'border-l-azul-surelia',
};

/**
 * ContratoDocumentos — attach complementary/accessory documents to a contrato and
 * manually link them to fianza lifecycle events. Nothing is automated: the broker
 * uploads, then chooses which fianza(s) a terminal document cancels/annuls, or
 * opens an endoso derived from a convenio modificatorio.
 */
export function ContratoDocumentos({ contratoData, clientData, convenio, user, userData, onUpdate }) {
  const isConv = Boolean(contratoData?.convenioId);
  const documentos = contratoData?.documentos || [];
  const fianzas = contratoData?.fianzas || [];
  const activeFianzas = fianzas.filter((f) => getEstadoFianza(f) === ESTADO_FIANZA.ACTIVA);

  const [tipo, setTipo] = useState(DOCUMENTO_TIPOS.ACTA_ENTREGA);
  const [file, setFile] = useState(null);
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selByDoc, setSelByDoc] = useState({});
  const [endoso, setEndoso] = useState(null);
  // Anulación requires a mandatory justification (fianzas cannot be anuladas freely).
  const [justByDoc, setJustByDoc] = useState({});
  // Anulación: whether the pagada prima stays as saldo a favor (default yes;
  // unchecked = the afianzadora refunded it, no credit).
  const [creditoByDoc, setCreditoByDoc] = useState({});

  const createdBy = { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null };

  const buildMeta = (description, extra = {}) =>
    isConv
      ? { convenioId: contratoData.convenioId, convenioName: convenio?.nombre || null, contratoId: contratoData.id, description, ...extra }
      : { clientId: clientData?.id || null, clientName: clientData?.nombreEmpresa || 'Sin nombre', contratoId: contratoData.id, description, ...extra };

  const basePath = `${contratoStorageBase(contratoData, clientData?.id)}/documentos`;

  const openFile = async (path) => {
    if (!path) return;
    try {
      const url = await getDownloadURL(ref(storage, path));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpload = async () => {
    if (!file) { setError('Selecciona un archivo.'); return; }
    setBusy(true);
    setError(null);
    try {
      const ts = Date.now();
      const docId = `doc_${ts}`;
      const storagePath = `${basePath}/${docId}_${sanitize(file.name)}`;
      await uploadBytes(ref(storage, storagePath), file);

      const documento = {
        id: docId,
        tipo,
        nombre: file.name,
        storagePath,
        uploadedAt: ts,
        uploadedBy: createdBy,
        nota: nota.trim() || null,
        fianzaEventos: [],
      };

      await mutateContract(db, contratoData.id, (contrato) => ({
        documentos: [...(contrato.documentos || []), documento],
      }));

      try {
        await logBitacora({
          action: 'UPLOAD',
          collection: 'contracts',
          documentId: contratoData.id,
          user,
          userData,
          metadata: buildMeta(`Adjuntó documento (${DOCUMENTO_TIPO_LABELS[tipo]}) '${file.name}'`, { documentoId: docId, documentoTipo: tipo }),
        });
      } catch (e) { console.error(e); }

      setFile(null);
      setNota('');
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error('documento upload failed', e);
      setError(e.message || 'No se pudo adjuntar el documento.');
    } finally {
      setBusy(false);
    }
  };

  const toggleSel = (docId, fianzaId) => {
    setSelByDoc((prev) => {
      const set = new Set(prev[docId] || []);
      if (set.has(fianzaId)) set.delete(fianzaId); else set.add(fianzaId);
      return { ...prev, [docId]: set };
    });
  };

  const applyTerminal = async (documento) => {
    const tipoEvento = TERMINAL_FOR_DOC[documento.tipo];
    if (!tipoEvento) return;
    const selected = Array.from(selByDoc[documento.id] || []);
    if (selected.length === 0) { setError('Selecciona al menos una fianza.'); return; }

    const isAnulacion = tipoEvento === EVENTO_TIPOS.ANULACION;
    const justificacion = (justByDoc[documento.id] || '').trim();
    if (isAnulacion && !justificacion) {
      setError('La anulación requiere una justificación.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const ts = Date.now();
      const links = [];

      if (isAnulacion) {
        // The backend owns the fianza write here, so this read only supplies the
        // `nextFianza` it will validate against its own transactional re-read.
        const snap = await getDoc(doc(db, 'contracts', contratoData.id));
        if (!snap.exists()) throw new Error('Contrato no encontrado.');
        const arr = [...(snap.data().fianzas || [])];
        // Anulación reverses comisión → route each fianza through the backend so
        // the reversal cargo is booked atomically with the fianza mutation. The
        // documento link is a metadata write that follows.
        for (let i = 0; i < selected.length; i++) {
          const fid = selected[i];
          const idx = arr.findIndex((f) => f.id === fid);
          if (idx === -1) continue;
          const evento = buildEvento({
            tipo: tipoEvento,
            origen: 'contrato',
            contratoDocumentoId: documento.id,
            filePaths: { documento: documento.storagePath },
            createdBy,
            justificacion,
            ts: ts + i,
          });
          const nextFianza = applyEndoso(arr[idx], evento);
          await recordComisionEvento({
            contractId: contratoData.id,
            fianzaId: fid,
            eventoTipo: 'anulacion',
            eventoId: evento.id,
            nextFianza,
            // Pagada prima → saldo a favor del cliente unless the owner said it
            // was refunded. The backend creates the credit atomically.
            primaCredito: creditoByDoc[documento.id] !== false,
            fecha: ts + i,
          });
          arr[idx] = nextFianza;
          links.push({ fianzaId: fid, eventoId: evento.id });
        }
        // The fianza mutations already landed server-side (one transaction per
        // fianza); this only back-links them onto the document.
        await mutateContract(db, contratoData.id, (contrato) => {
          const docsNow = [...(contrato.documentos || [])];
          const dIdx = docsNow.findIndex((d) => d.id === documento.id);
          if (dIdx === -1) return null;
          docsNow[dIdx] = {
            ...docsNow[dIdx],
            fianzaEventos: [...(docsNow[dIdx].fianzaEventos || []), ...links],
          };
          return { documentos: docsNow };
        });
      } else {
        // Cancelación — natural end, no ledger movement. Transactional because it
        // rewrites fianzas[] AND documentos[]: those two must not be able to
        // drift apart, and a concurrent endoso must not lose either one.
        await mutateContract(db, contratoData.id, (contrato) => {
          const fianzasNow = [...(contrato.fianzas || [])];
          const docsNow = [...(contrato.documentos || [])];
          links.length = 0;
          selected.forEach((fid, i) => {
            const idx = fianzasNow.findIndex((f) => f.id === fid);
            if (idx === -1) return;
            // Skip anything a concurrent write already took terminal.
            if (getEstadoFianza(fianzasNow[idx]) !== ESTADO_FIANZA.ACTIVA) return;
            const evento = buildEvento({
              tipo: tipoEvento,
              origen: 'contrato',
              contratoDocumentoId: documento.id,
              filePaths: { documento: documento.storagePath },
              createdBy,
              ts: ts + i,
            });
            fianzasNow[idx] = applyEndoso(fianzasNow[idx], evento);
            links.push({ fianzaId: fid, eventoId: evento.id });
          });
          const dIdx = docsNow.findIndex((d) => d.id === documento.id);
          if (dIdx !== -1) {
            docsNow[dIdx] = {
              ...docsNow[dIdx],
              fianzaEventos: [...(docsNow[dIdx].fianzaEventos || []), ...links],
            };
          }
          return { fianzas: fianzasNow, documentos: docsNow };
        });
      }

      const verbo = tipoEvento === EVENTO_TIPOS.CANCELACION ? 'Canceló' : 'Anuló';
      for (const l of links) {
        // Label only — the authoritative write already happened above.
        const f = fianzas.find((x) => x.id === l.fianzaId);
        try {
          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: contratoData.id,
            user,
            userData,
            metadata: buildMeta(`${verbo} fianza #${f?.numeroFianza || 'sin número'} (${DOCUMENTO_TIPO_LABELS[documento.tipo]})`, {
              fianzaId: l.fianzaId,
              documentoId: documento.id,
            }),
          });
        } catch (e) { console.error(e); }
      }

      setSelByDoc((prev) => ({ ...prev, [documento.id]: new Set() }));
      setJustByDoc((prev) => ({ ...prev, [documento.id]: '' }));
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error('terminal apply failed', e);
      setError(e.message || 'No se pudo aplicar el documento a la fianza.');
    } finally {
      setBusy(false);
    }
  };

  const linkedFianzaLabels = (documento) =>
    (documento.fianzaEventos || [])
      .map((le) => fianzas.find((f) => f.id === le.fianzaId)?.numeroFianza || le.fianzaId)
      .join(', ');

  return (
    <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-3">
        <PaperClipIcon className="w-4 h-4 text-azul-surelia" />
        Documentos Complementarios / Accesorios
      </h3>

      {/* Upload row */}
      <div className="rounded-xl border border-gray-200 bg-zinc-50 p-3 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia">
              {Object.values(DOCUMENTO_TIPOS).map((t) => (
                <option key={t} value={t}>{DOCUMENTO_TIPO_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Archivo</label>
            {file ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-cian-digital/40 bg-cian-digital/5 px-3 py-2">
                <span className="text-xs text-gray-700 truncate">{file.name}</span>
                <button type="button" onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500 shrink-0">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <CompactFileReceiver type="image-or-pdf" onFilesChange={(files) => setFile(files?.[0] || null)} placeholder="Arrastra o elige archivo" />
            )}
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota (opcional)</label>
            <input type="text" value={nota} onChange={(e) => setNota(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia" />
          </div>
          <div className="sm:col-span-2 sm:pt-5">
            <Button color="surelia" onClick={handleUpload} disabled={busy || !file} className="w-full">
              {busy ? 'Subiendo…' : 'Adjuntar'}
            </Button>
          </div>
        </div>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}

      {/* List */}
      {documentos.length === 0 ? (
        <div className="text-xs text-gray-500">Sin documentos complementarios.</div>
      ) : (
        <ul className="space-y-2">
          {documentos.map((d) => {
            const isTerminal = Boolean(TERMINAL_FOR_DOC[d.tipo]);
            const isModificatorio = d.tipo === DOCUMENTO_TIPOS.CONVENIO_MODIFICATORIO;
            const linked = linkedFianzaLabels(d);
            const sel = selByDoc[d.id] || new Set();
            return (
              <li key={d.id} className={`bg-white border border-gray-200 border-l-4 ${DOC_ACCENT[d.tipo] || 'border-l-azul-surelia'} rounded-lg p-3`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                      {DOCUMENTO_TIPO_LABELS[d.tipo] || d.tipo}
                      <span className="ml-2 text-xs font-normal text-gray-400 truncate">{d.nombre}</span>
                    </div>
                    {d.nota && <div className="text-xs text-gray-500 italic">{d.nota}</div>}
                    {linked && <div className="text-xs text-gray-500">Aplicado a fianza(s): {linked}</div>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-xs text-gray-400">{d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString('es-MX') : ''}</div>
                    <button type="button" onClick={() => openFile(d.storagePath)} className="text-xs font-medium text-cian-digital hover:underline">Ver</button>
                  </div>
                </div>

                {/* Terminal application (acta de entrega → cancelación, anulación) */}
                {isTerminal && activeFianzas.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs font-medium text-gray-600 mb-1.5">
                      {d.tipo === DOCUMENTO_TIPOS.ACTA_ENTREGA ? 'Cancelar' : 'Anular'} fianza(s):
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {activeFianzas.map((f) => {
                        const on = sel.has(f.id);
                        return (
                          <button key={f.id} type="button" onClick={() => toggleSel(d.id, f.id)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-azul-surelia text-white border-azul-surelia' : 'bg-white text-gray-700 border-gray-300 hover:border-azul-surelia'}`}>
                            #{f.numeroFianza || 'sin número'}
                          </button>
                        );
                      })}
                    </div>
                    {d.tipo === DOCUMENTO_TIPOS.ANULACION && (
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Justificación de la anulación (obligatoria)
                        </label>
                        <textarea
                          rows={2}
                          value={justByDoc[d.id] || ''}
                          onChange={(e) => setJustByDoc((prev) => ({ ...prev, [d.id]: e.target.value }))}
                          placeholder="Motivo por el que la fianza se anula (nunca debió emitirse, error, etc.)"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                        />
                        <label className="mt-2 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={creditoByDoc[d.id] !== false}
                            onChange={(e) => setCreditoByDoc((prev) => ({ ...prev, [d.id]: e.target.checked }))}
                          />
                          La prima pagada queda como saldo a favor del cliente
                        </label>
                      </div>
                    )}
                    <Button color="red" onClick={() => applyTerminal(d)} disabled={busy || sel.size === 0}>
                      {d.tipo === DOCUMENTO_TIPOS.ACTA_ENTREGA ? 'Cancelar fianza(s)' : 'Anular fianza(s)'}
                    </Button>
                  </div>
                )}

                {/* Convenio modificatorio → register a derived endoso */}
                {isModificatorio && activeFianzas.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Registrar endoso derivado:</span>
                    {activeFianzas.map((f) => (
                      <button key={f.id} type="button" onClick={() => setEndoso({ fianza: f, documentoId: d.id })}
                        className="text-xs px-2.5 py-1 rounded-full border border-cian-digital/50 text-azul-surelia hover:bg-cian-digital/10">
                        Fianza #{f.numeroFianza || 'sin número'}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {endoso && (
        <EndosoDialog
          isOpen={Boolean(endoso)}
          onClose={() => setEndoso(null)}
          fianza={endoso.fianza}
          contratoData={contratoData}
          clientData={clientData}
          user={user}
          userData={userData}
          origen="contrato"
          contratoDocumentoId={endoso.documentoId}
          buildBitacoraMeta={buildMeta}
          onApplied={() => { if (onUpdate) onUpdate(); }}
        />
      )}
    </div>
  );
}

export default ContratoDocumentos;
